import { useCallback, useEffect, useState } from 'react';
import {
  fetchInboxApprovals,
  fetchInboxApprovalCount,
  fetchMineApprovals,
  fetchSentApprovals,
} from '../../api/approval';
import ListPager from '../common/ListPager';
import {
  ApprovalRequest,
  ApprovalStep,
  approvalStatusLabel,
  approvalStepTypeLabel,
  approvalTargetLabel,
} from '../../types/approval';
import { DEFAULT_PAGE_SIZE } from '../../types/page';
import './ApprovalPanel.css';

import { reportError } from '../../utils/notify';
import { notifyApprovalInboxChanged } from '../../utils/approvalInboxBadge';
export type ApprovalFilter = 'all' | 'inbox' | 'sent';

interface Props {
  initialTab?: ApprovalFilter;
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  return value.replace('T', ' ').slice(0, 16);
}

function stripRoleSuffix(value?: string | null): string {
  if (!value) return '';
  const text = value.trim();
  if (text.includes(',')) {
    return text
      .split(',')
      .map((part) => stripRoleSuffix(part.trim()))
      .filter(Boolean)
      .join(', ');
  }
  const index = text.indexOf(' (');
  if (index > 0 && text.endsWith(')')) {
    return text.slice(0, index).trim();
  }
  return text;
}

function statusClass(status?: string | null) {
  switch (status) {
    case 'IN_PROGRESS':
      return 'is-progress';
    case 'COMPLETED':
      return 'is-done';
    case 'REJECTED':
      return 'is-reject';
    case 'CANCELLED':
      return 'is-cancel';
    default:
      return '';
  }
}

function currentStepLabel(item: ApprovalRequest) {
  if (item.status !== 'IN_PROGRESS') return '-';
  const pending = (item.steps || []).find((s: ApprovalStep) => s.status === 'PENDING');
  if (pending) return approvalStepTypeLabel(pending.stepType);
  return item.currentPendingNames || '-';
}

/** 현재 결재 대상자(대기 중) 또는 최종 승인자 */
function assigneeLabel(item: ApprovalRequest) {
  if (item.status === 'IN_PROGRESS') {
    const pending = (item.steps || [])
      .filter((s) => s.status === 'PENDING')
      .map((s) => stripRoleSuffix(s.assigneeName || ''))
      .filter(Boolean);
    if (pending.length) return Array.from(new Set(pending)).join(', ');
    if (item.currentPendingNames) return stripRoleSuffix(item.currentPendingNames);
  }
  const finals = (item.steps || [])
    .filter((s) => s.stepType === 'FINAL')
    .map((s) => stripRoleSuffix(s.assigneeName || ''))
    .filter(Boolean);
  if (finals.length) return Array.from(new Set(finals)).join(', ');
  const any = (item.steps || [])
    .map((s) => stripRoleSuffix(s.assigneeName || ''))
    .filter(Boolean);
  return any.length ? Array.from(new Set(any)).join(', ') : '-';
}

function completedAtLabel(item: ApprovalRequest) {
  if (item.status !== 'COMPLETED' && item.status !== 'REJECTED') return '-';
  return formatDateTime(item.completedAt);
}

function ApprovalPanel({ initialTab = 'all' }: Props) {
  const [filter, setFilter] = useState<ApprovalFilter>(initialTab);
  const [rows, setRows] = useState<ApprovalRequest[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [mineTotal, setMineTotal] = useState(0);
  const [inboxTotal, setInboxTotal] = useState(0);
  const [sentTotal, setSentTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    setFilter(initialTab);
  }, [initialTab]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  const loadTabCounts = useCallback(async () => {
    try {
      const [minePage, inboxPage, sentPage, inboxCount] = await Promise.all([
        fetchMineApprovals({ page: 0, size: 1 }),
        fetchInboxApprovals({ page: 0, size: 1 }),
        fetchSentApprovals({ page: 0, size: 1 }),
        fetchInboxApprovalCount(),
      ]);
      setMineTotal(minePage.totalElements ?? 0);
      setInboxTotal(inboxCount ?? inboxPage.totalElements ?? 0);
      setSentTotal(sentPage.totalElements ?? 0);
      notifyApprovalInboxChanged(inboxCount ?? inboxPage.totalElements ?? 0);
    } catch {
      // 탭 배지 실패는 목록 로드에서 처리
    }
  }, []);

  const load = useCallback(
    async (pageNum = 0) => {
      try {
        setLoading(true);
        const params = { page: pageNum, size: DEFAULT_PAGE_SIZE, q: debouncedQuery || undefined };
        const result =
          filter === 'inbox'
            ? await fetchInboxApprovals(params)
            : filter === 'sent'
              ? await fetchSentApprovals(params)
              : await fetchMineApprovals(params);
        setRows(result.content || []);
        setPage(result.page ?? pageNum);
        setTotalPages(result.totalPages ?? 0);
        setTotalElements(result.totalElements ?? 0);
        if (!debouncedQuery) {
          if (filter === 'inbox') setInboxTotal(result.totalElements ?? 0);
          else if (filter === 'sent') setSentTotal(result.totalElements ?? 0);
          else setMineTotal(result.totalElements ?? 0);
        }
        setError('');
        if (filter === 'inbox' && !debouncedQuery) {
          notifyApprovalInboxChanged(result.totalElements ?? 0);
        }
      } catch {
        reportError(setError, '결재 목록을 불러오지 못했습니다.');
        setRows([]);
        setTotalPages(0);
        setTotalElements(0);
      } finally {
        setLoading(false);
      }
    },
    [filter, debouncedQuery]
  );

  useEffect(() => {
    void loadTabCounts();
  }, [loadTabCounts]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(0), 50);
    return () => window.clearTimeout(timer);
  }, [load]);

  const openDetailWindow = (item: ApprovalRequest) => {
    const url = `${window.location.origin}/approvals/${item.requestId}`;
    window.open(url, `approval-${item.requestId}`, 'noopener,width=1280,height=860');
  };

  const emptyHint =
    filter === 'inbox'
      ? '지금 처리할 결재가 없습니다.'
      : filter === 'sent'
        ? '올린 결재가 없습니다. 설비 문서에서 ‘결재 요청’ 후 여기에 표시됩니다.'
        : '관여한 결재 문서가 없습니다.';

  const changeFilter = (next: ApprovalFilter) => {
    if (next === filter) return;
    setFilter(next);
  };

  return (
    <div className="approval-panel">
      <div className="approval-panel__head">
        <div>
          <h2>결재</h2>
          <p className="approval-panel__guide">
            결재 문서를 게시판처럼 목록으로 관리합니다. 행을 누르면 상세·결재 창이 열립니다.
          </p>
        </div>
        <label className="approval-panel__search">
          <span className="visually-hidden">검색</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="제목·요청자 검색"
          />
        </label>
      </div>

      <div className="approval-panel__filters">
        <button
          type="button"
          className={filter === 'all' ? 'is-active' : ''}
          onClick={() => changeFilter('all')}
        >
          전체
          <em>{mineTotal}</em>
        </button>
        <button
          type="button"
          className={filter === 'inbox' ? 'is-active' : ''}
          onClick={() => changeFilter('inbox')}
        >
          결재할 문서
          <em>{inboxTotal}</em>
        </button>
        <button
          type="button"
          className={filter === 'sent' ? 'is-active' : ''}
          onClick={() => changeFilter('sent')}
        >
          내가 올린
          <em>{sentTotal}</em>
        </button>
        <button
          type="button"
          className="approval-panel__reload"
          onClick={() => {
            void loadTabCounts();
            void load(page);
          }}
          disabled={loading}
        >
          새로고침
        </button>
      </div>

      <div className="approval-panel__body">
        {error && <p className="approval-panel__error">{error}</p>}

        <div className="paged-list">
          <div className="paged-list__scroll">
            {loading ? <p className="paged-list__status">불러오는 중...</p> : null}
            {!loading && rows.length === 0 ? (
              <p className="paged-list__status">{emptyHint}</p>
            ) : null}
            {!loading && rows.length > 0 ? (
              <table className="ec-table approval-panel__table">
                <thead>
                  <tr>
                    <th className="is-no">No</th>
                    <th className="is-status">상태</th>
                    <th className="is-type">문서유형</th>
                    <th>제목</th>
                    <th className="is-user">요청자</th>
                    <th className="is-assignee">대상자</th>
                    <th className="is-date">요청일</th>
                    <th className="is-date">완료일</th>
                    <th className="is-step">단계</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item, index) => (
                    <tr
                      key={item.requestId}
                      onClick={() => openDetailWindow(item)}
                      title="클릭하면 결재 상세가 열립니다"
                    >
                      <td className="is-no">
                        {Math.max(0, totalElements - page * DEFAULT_PAGE_SIZE - index)}
                      </td>
                      <td className="is-status">
                        <span
                          className={`approval-panel__badge ${
                            item.myPendingStepId && item.status === 'IN_PROGRESS'
                              ? 'is-wait'
                              : statusClass(item.status)
                          }`}
                        >
                          {item.myPendingStepId && item.status === 'IN_PROGRESS'
                            ? '대기'
                            : approvalStatusLabel(item.status)}
                        </span>
                      </td>
                      <td className="is-type">{approvalTargetLabel(item.targetType)}</td>
                      <td className="is-title">
                        <span className="approval-panel__title">{item.title}</span>
                        {item.targetInfo?.summary ? (
                          <span className="approval-panel__summary">{item.targetInfo.summary}</span>
                        ) : null}
                      </td>
                      <td className="is-user">{stripRoleSuffix(item.requesterName) || '-'}</td>
                      <td className="is-assignee">{assigneeLabel(item)}</td>
                      <td className="is-date">{formatDateTime(item.createdAt)}</td>
                      <td className="is-date">{completedAtLabel(item)}</td>
                      <td className="is-step">{currentStepLabel(item)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
          <ListPager
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            loading={loading}
            onChange={(next) => void load(next)}
          />
        </div>
      </div>
    </div>
  );
}

export default ApprovalPanel;
